import React from 'react';
import classNames from 'classnames/bind';
import { DateTime } from 'luxon';
import { filesize } from 'filesize';

import { FilesSearchResult } from 'types/files_search_result';
import { LumeoFile } from 'types/file';
import { VideoSource } from 'hooks/api/useVideoSources';
import type { TrackableItem, TrackableList } from 'types/trackables';

import { useModal } from 'hooks/useModal';

import { Checkbox } from 'components/Checkbox/Checkbox';
import { DateRange } from 'components/DateTimeRangeInput/DateTimeRangeInput';
import { FileSearchMetadata } from 'files/components/FileSearchMetadata';
import { Heading } from 'components/Heading/Heading';
import { Icon } from 'components/Icon/IconDeprecated';
import { Text } from 'components/Text/Text';
import { TextAppLink } from 'components/TextAppLink/TextAppLink';
import { Thumbnail } from 'components/Thumbnail/Thumbnail';

import { useFilesSearchState } from './search_context';
import styles from './Search.module.scss';

const c = classNames.bind(styles);

type FilesSearchItemProps = Pick<
  LumeoFile,
  'id' | 'name' | 'mime_type' | 'thumbnail_url' | 'stream_id' | 'created_at'
> &
  Partial<
    Pick<
      FilesSearchResult,
      'events' | 'objects' | 'texts' | 'trackables' | 'search_score'
    >
  > & {
    source?: VideoSource;
    initialDateRange?: DateRange;
    isDeleted: boolean;
    size: number;
    trackableItemsLookup?: Record<
      string,
      Pick<TrackableItem, 'id' | 'name' | 'trackable_list_id'>
    >;
    trackableListsLookup?: Record<string, Pick<TrackableList, 'id' | 'name'>>;
  };

export function FilesSearchItem({
  id,
  name,
  thumbnail_url,
  mime_type,
  created_at,
  events = [],
  objects = [],
  texts = [],
  trackables = [],
  search_score,
  stream_id,
  source,
  initialDateRange,
  size,
  isDeleted,
  trackableItemsLookup,
  trackableListsLookup,
}: FilesSearchItemProps) {
  const { selectedFileIds, dispatch } = useFilesSearchState();

  const { open } = useModal();

  const isChecked = selectedFileIds.includes(id);

  function handleCheckboxChange({
    target: { checked },
  }: React.ChangeEvent<HTMLInputElement>) {
    if (checked) {
      dispatch({ type: 'add', ids: [id] });
    } else {
      dispatch({ type: 'remove', id });
    }
  }

  const sourceType = source && 'stream_type' in source ? 'stream' : 'camera';

  return (
    <div className={c('item', { deleted: isDeleted })}>
      {!isDeleted && (
        <Checkbox
          id={`bulk_${id}`}
          className={c('item-checkbox', { checked: isChecked })}
          checked={isChecked}
          onChange={handleCheckboxChange}
          aria-label={`Select file ${name}`}
        />
      )}

      <button
        className={c('item-view-button')}
        onClick={() =>
          open('files-search', {
            defaultFile: { id, created_at },
            defaultStreamId: stream_id,
            initialDateRange,
          })
        }
        disabled={isDeleted}
      >
        <div className="relative">
          <Thumbnail
            mime_type={mime_type}
            thumbnail_url={thumbnail_url}
            isDeleted={isDeleted}
          />
          {search_score !== null &&
            search_score !== undefined &&
            search_score > 0 && (
            <span className="absolute top-1.5 right-1.5 rounded bg-black/70 px-1.5 py-0.5 text-xs font-medium text-white">
              {Math.round(search_score * 100)}% Match
            </span>
          )}
        </div>
        <Text size="small">
          {DateTime.fromISO(created_at).toLocaleString(DateTime.DATETIME_MED)} /{' '}
          {filesize(size)}
        </Text>

        <Heading className={c('item-name')} level="3">
          {name}
        </Heading>
      </button>

      {source && (
        <div className={c('item-source')}>
          <Icon name={sourceType} />
          <TextAppLink to={`/deploy/${sourceType}s/${source.id}`}>
            {source.name}
          </TextAppLink>
        </div>
      )}

      <FileSearchMetadata
        layout="compact"
        objects={objects}
        events={events}
        texts={texts}
        trackables={trackables}
        trackableItemsLookup={trackableItemsLookup}
        trackableListsLookup={trackableListsLookup}
      />
    </div>
  );
}
